/** Portefeuille syndic embarqué dans une fiche place-copro, ou renvoyé par GET /api/copro-portfolio. */

export const COPRO_PORTFOLIO_PAGE_SIZE = 50;

/** Filtre mandat de GET /api/copro-portfolio. `en_cours` est la première page embarquée. */
export type CoproPortfolioMandatFilter = "en_cours" | "tous";

export interface CoproPortfolioSociete {
  siren?: string | null;
  denomination?: string | null;
  sigle?: string | null;
  categorieEntreprise?: string | null;
  activitePrincipale?: string | null;
  etatAdministratif?: string | null;
}

export interface CoproPortfolioEtablissement {
  siret?: string | null;
  adresse?: string | null;
  siege?: boolean | null;
}

export interface CoproPortfolioTotals {
  mandatEnCours?: number | null;
  total?: number | null;
}

/** Fiche légère du portefeuille. Noms courts, distincts des champs Mongo de la fiche place-copro. */
export interface CoproPortfolioCopro {
  immatriculation: string | null;
  nomUsage: string | null;
  adresse: string | null;
  commune: string | null;
  codePostal: string | null;
  lots: number | null;
  dateFinMandat: string | null;
  siret: string | null;
  lat: number | null;
  long: number | null;
}

export interface CoproPortfolio {
  societe?: CoproPortfolioSociete | null;
  etablissement?: CoproPortfolioEtablissement | null;
  totals?: CoproPortfolioTotals | null;
  /** Page 1-based. La première page embarquée dans place-copro est 1. */
  page?: number | null;
  limit?: number | null;
  mandat?: CoproPortfolioMandatFilter | null;
  copros?: CoproPortfolioCopro[] | null;
}

export interface CoproPortfolioOpenRequest {
  siret: string;
  portfolio: CoproPortfolio;
}

export interface CoproPortfolioMapPoint {
  id: string;
  lat: number;
  long: number;
}

function numberLong(value: object): string | null {
  if (!("numberLong" in value)) return null;
  const raw = (value as { numberLong?: unknown }).numberLong;
  if (raw == null) return null;
  const text = String(raw).trim();
  return text || null;
}

/** SIRET à 14 chiffres, ou null (bénévole, non connu, valeur illisible). */
export function extractSiret(value: unknown): string | null {
  if (value == null) return null;
  const raw = typeof value === "object" ? numberLong(value) : String(value);
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  return digits.length === 14 ? digits : null;
}

export function readCoproPortfolio(fiche: Record<string, unknown> | null | undefined): CoproPortfolio | null {
  const raw = fiche?.portfolio;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw as CoproPortfolio;
}

/** Bandeau affiché seulement s'il y a un SIRET et un portfolio déjà présent sur la fiche. */
export function portfolioContextFromFiche(
  fiche: Record<string, unknown> | null | undefined
): CoproPortfolioOpenRequest | null {
  if (!fiche) return null;
  const siret = extractSiret(fiche.siret_du_representant_legal);
  const portfolio = readCoproPortfolio(fiche);
  if (!siret || !portfolio) return null;
  return {
    siret,
    portfolio,
  };
}

export function formatSiren(siren: unknown): string {
  if (siren == null) return "";
  const digits = String(siren).replace(/\D/g, "");
  if (digits.length !== 9) return String(siren).trim();
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export function formatSiret(siret: unknown): string {
  const digits = extractSiret(siret) ?? (siret == null ? "" : String(siret).replace(/\D/g, ""));
  if (digits.length !== 14) return siret == null ? "" : String(siret).trim();
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
}

export function coprosGereesLabel(count: number | null | undefined): string {
  const n = typeof count === "number" && Number.isFinite(count) ? count : 0;
  const formatted = new Intl.NumberFormat("fr-FR").format(n);
  return n === 1 ? `${formatted} copropriété gérée` : `${formatted} copropriétés gérées`;
}

export function formatPortfolioDate(value: unknown): string {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatPortfolioLots(value: unknown): string {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : Number(String(value).replace(/\s/g, "").replace(",", "."));
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat("fr-FR").format(n);
}

export function parsePortfolioCoord(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isEtablissementSiege(value: unknown): boolean {
  return value === true || value === "true";
}

export function asPortfolioText(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

export function samePortfolioPoints(a: CoproPortfolioMapPoint[], b: CoproPortfolioMapPoint[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].id !== b[i].id || a[i].lat !== b[i].lat || a[i].long !== b[i].long) return false;
  }
  return true;
}

export function normalizeCoproPortfolio(data: unknown): CoproPortfolio | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const obj = data as CoproPortfolio;
  if (!("copros" in obj) && !("societe" in obj) && !("totals" in obj)) return null;
  return obj;
}
